import { useFormik, Form, Field, FormikProvider } from 'formik';
import { Button, Input } from 'styled';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import axios from 'apis/axios';

/** Mirrors the backend password policy so the user gets instant feedback (utils/helper.js -> validatePasswordPolicy). */
const validatePasswordPolicy = (password) => {
  if (!password || password.length < 12) {
    return 'Password must be at least 12 characters long';
  }
  const categories = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/];
  const matched = categories.filter((re) => re.test(password)).length;
  if (matched < 3) {
    return 'Password must include at least 3 of: uppercase, lowercase, number, special character';
  }
  return null;
};

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  // token + userId are handed over by the login flow when the password has expired.
  const { token, userId } = location.state || {};

  // Reached this page without going through an expired-password login -> nothing to change.
  useEffect(() => {
    if (!token || !userId) {
      navigate('/login', { replace: true });
    }
  }, [token, userId, navigate]);

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    onSubmit: async (values) => {
      if (!token || !userId) return;
      setIsLoading(true);
      try {
        await axios.put(
          `user/change-password/${userId}`,
          { password: values.password },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Password changed successfully');
        // Send them back to log in with the new password.
        navigate('/login', { replace: true });
      } catch (err) {
        // Backend messages already say "password", so they pass through as-is.
        toast.error(
          err.response?.data?.message || err.message || 'Could not change the password'
        );
      } finally {
        setIsLoading(false);
      }
    },
    validate: (values) => {
      const errors = {};
      const policyError = validatePasswordPolicy(values.password);
      if (!values.password) {
        errors.password = 'Required';
      } else if (policyError) {
        errors.password = policyError;
      }

      if (!values.confirmPassword) {
        errors.confirmPassword = 'Required';
      } else if (values.confirmPassword !== values.password) {
        errors.confirmPassword = 'Passwords do not match';
      }
      return errors;
    },
  });

  return (
    <div
      className="flex justify-center items-center h-screen w-screen"
      style={{
        backgroundImage: `url(bg.png)`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundBlendMode: 'multiply',
        backgroundColor: 'hsl(200, 100%, 30%)',
      }}
    >
      <FormikProvider value={formik}>
        <Form className="mx-4 sm:mx-0 w-full sm:w-1/2 lg:w-1/3 relative bg-white bg-opacity-40 rounded-md flex flex-col justify-center items-center">
          <div className="p-4 2xl:p-6 flex flex-col w-full space-y-3 justify-center items-center text-gray-800">
            <h2 className="w-full text-xl font-semibold text-gray-800">Set a New Password</h2>
            <p className="w-full text-sm text-gray-700">
              Your password has expired. Please set a new password to continue.
            </p>

            <label htmlFor="password" className="w-full">
              New Password
            </label>
            <Field
              as={Input}
              type="password"
              name="password"
              id="password"
              placeholder="Enter new password"
              autoComplete="new-password"
            />
            {formik.touched.password && formik.errors.password ? (
              <div className="flex items-start w-full">
                <strong className="text-red-500">{formik.errors.password}</strong>
              </div>
            ) : (
              ''
            )}

            <label htmlFor="confirmPassword" className="w-full">
              Confirm Password
            </label>
            <Field
              as={Input}
              type="password"
              name="confirmPassword"
              id="confirmPassword"
              placeholder="Re-enter new password"
              autoComplete="new-password"
            />
            {formik.touched.confirmPassword && formik.errors.confirmPassword ? (
              <div className="flex items-start w-full">
                <strong className="text-red-500">{formik.errors.confirmPassword}</strong>
              </div>
            ) : (
              ''
            )}

            <p className="w-full text-xs text-gray-700">
              Password must use at least 12 characters with a mix of uppercase, lowercase, numbers and symbols
              (any 3 of these).
            </p>

            <Button
              id="changePasswordButton"
              className={`bg-[#FDBC00] ${!isLoading && 'hover:bg-green-700'} text-white p-2 rounded-md w-1/2`}
              style={{ marginTop: '1.25rem' }}
              onClick={formik.handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Change Password'}
            </Button>
          </div>
        </Form>
      </FormikProvider>
    </div>
  );
};

export default ChangePassword;
